

import { Button, Alert, Combobox, FormField, TextInput, NumberInput } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateSellerStoreMutation } from "../services/useCreateSellerStoreMutation";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";

export function StoreCreateForm({
  sellerId,
  locale,
  plans,
  onSuccess,
}: {
  sellerId: string;
  locale: Locale;
  plans: { id: string; nameAr: string; nameEn: string | null }[];
  onSuccess?: () => void;
}) {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const { mutateAsync: createStore } = useCreateSellerStoreMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["storeName", "subscriptionPlanId", "subscriptionReason"],
      t(keys.errors.required)
    );

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      return;
    }

    try {
      await createStore({
        sellerId,
        storeName: String(formData.get("storeName") ?? ""),
        maxProductImageCount: (() => {
          const rawValue = formData.get("maxProductImageCount");
          if (typeof rawValue !== "string" || rawValue.trim() === "") {
            return undefined;
          }
          const parsed = Number(rawValue);
          return Number.isFinite(parsed) ? parsed : undefined;
        })(),
        subscriptionPlanId: String(formData.get("subscriptionPlanId") ?? ""),
        subscriptionReason: String(formData.get("subscriptionReason") ?? ""),
        subscriptionStartDate: (() => {
          const rawValue = formData.get("subscriptionStartDate");
          if (typeof rawValue !== "string" || rawValue.trim() === "") {
            return undefined;
          }
          return rawValue;
        })(),
        subscriptionOverrideAmount: (() => {
          const rawValue = formData.get("subscriptionOverrideAmount");
          if (typeof rawValue !== "string" || rawValue.trim() === "") {
            return undefined;
          }
          const parsed = Number(rawValue);
          return Number.isFinite(parsed) ? parsed : undefined;
        })(),
        subscriptionOverridePeriodDays: (() => {
          const rawValue = formData.get("subscriptionOverridePeriodDays");
          if (typeof rawValue !== "string" || rawValue.trim() === "") {
            return undefined;
          }
          const parsed = Number(rawValue);
          return Number.isFinite(parsed) ? parsed : undefined;
        })(),
      });
      onSuccess?.();
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      return;
    }
  };

  return (
    <form className="mx-auto flex w-full max-w-[640px] flex-col gap-4" onSubmit={handleSubmit}>
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      <FormField
        label={t(keys.common.storeName)}
        name="storeName"
        required
        error={fieldErrors.storeName}
      >
        <TextInput
          name="storeName"
          placeholder={t(keys.placeholders.storeName)}
          required
          hasError={Boolean(fieldErrors.storeName)}
        />
      </FormField>
      <FormField
        label={t(keys.admin.maxProductImageCountLabel)}
        name="maxProductImageCount"
        helperText={t(keys.admin.maxProductImageCountHelper)}
      >
        <NumberInput
          name="maxProductImageCount"
          placeholder="3"
        />
      </FormField>
      <FormField
        label={t(keys.admin.subscriptionSelectLabel)}
        name="subscriptionPlanId"
        required
        error={fieldErrors.subscriptionPlanId}
      >
        <input type="hidden" name="subscriptionPlanId" value={subscriptionPlanId} />
        <Combobox
          options={[
            { value: "", label: t(keys.admin.subscriptionSelectPlaceholder) },
            ...plans.map((plan) => ({
              value: plan.id,
              label: pickLocalized(locale, plan.nameAr, plan.nameEn),
            })),
          ]}
          value={subscriptionPlanId}
          onChange={(value) => setSubscriptionPlanId(value ?? "")}
        />
      </FormField>
      <FormField
        label={t(keys.admin.subscriptionStartDateLabel)}
        name="subscriptionStartDate"
        helperText={t(keys.admin.subscriptionStartDateHelper)}
      >
        <TextInput name="subscriptionStartDate" type="date" />
      </FormField>
      <FormField
        label={t(keys.admin.subscriptionReasonLabel)}
        name="subscriptionReason"
        required
        error={fieldErrors.subscriptionReason}
      >
        <TextInput
          name="subscriptionReason"
          placeholder={t(keys.placeholders.subscriptionReason)}
          required
          hasError={Boolean(fieldErrors.subscriptionReason)}
        />
      </FormField>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label={t(keys.admin.subscriptionOverrideAmountLabel)}
          name="subscriptionOverrideAmount"
          helperText={t(keys.admin.subscriptionOverrideAmountHelper)}
        >
          <NumberInput name="subscriptionOverrideAmount" placeholder="0" allowDecimal />
        </FormField>
        <FormField
          label={t(keys.admin.subscriptionOverridePeriodLabel)}
          name="subscriptionOverridePeriodDays"
          helperText={t(keys.admin.subscriptionOverridePeriodHelper)}
        >
          <NumberInput name="subscriptionOverridePeriodDays" placeholder="30" />
        </FormField>
      </div>
      <Button
        type="submit"
        variant="primary" className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition"
      >
        {t(keys.common.create)}
      </Button>
    </form>
  );
}
